import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageGrid, splitStrategy } from "../../commons/layout/PageGrid.js";
// import { OrganisationAgreementTemplatesBox } from "../../features/organisations/boxes/OrganisationAgreementTemplatesBox.js";
import { OrganisationCustomFieldConfigBox } from "../../features/organisations/boxes/OrganisationCustomFieldConfigBox.js";
import { OrganisationAutomatedWorkflowsBox } from "../../features/organisations/boxes/OrganisationAutomatedWorkflowsBox.js";
import { OrganisationEmailSettingsBox } from "../../features/organisations/boxes/OrganisationEmailSettingsBox.js";
import { OrganisationEmbeddableWizardSnippetBox } from "../../features/organisations/boxes/OrganisationEmbeddableWizardSnippetBox.js";
import { OrganisationIntegrationsBox } from "../../features/organisations/boxes/OrganisationIntegrationsBox.js";
import { OrganisationProjectDefaultSettingsBox } from "../../features/organisations/boxes/OrganisationProjectDefaultSettingsBox.js";
import { OrganisationProjectImportSettingsBox } from "../../features/organisations/boxes/OrganisationProjectImportSettingsBox.js";
import { OrganisationReferralBox } from "../../features/organisations/boxes/OrganisationReferralBox.js";
import {
  useOrganisationSettingsIntegrationsQuery,
  useOrganisationSettingsIntegrations_InvalidatePrefixOrgCacheMutation,
} from "./OrganisationSettingsIntegrations.generated.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

export const OrganisationSettingsIntegrations = ({ submenuItems }: Props) => {
  const { t } = useTranslate("OrganisationPageTopbar");

  const client = useApolloClient();
  const query = useOrganisationSettingsIntegrationsQuery({
    client,
    fetchPolicy: "cache-and-network",
  });

  const [
    invalidatePrefixOrgCache,
    { loading: invalidatePrefixOrgCacheLoading },
  ] = useOrganisationSettingsIntegrations_InvalidatePrefixOrgCacheMutation({
    client,
  });

  const organisation = query.data?.viewer?.organisation;
  const user = query.data?.viewer;
  const organisationSettings = query.data?.organisationSettings;
  const organisationEmailSettings = getDataOrNull(
    query.data?.organisationEmailSettings
  );
  const organisationEmailTemplates =
    query.data?.organisationEmailTemplates ?? [];
  const organisationWorkflows = query.data?.organisationWorkflows ?? [];
  const integrations =
    getDataOrNull(query.data?.integrations)?.edges.map(e => e.node) ?? [];
  const organisationIntegrations =
    getDataOrNull(query.data?.organisationIntegrations)?.edges.map(
      e => e.node
    ) ?? [];

  return (
    <Page title={t("Data & Integrations")} submenuItems={submenuItems}>
      {organisation && user && organisationSettings && (
        <PageContainer>
          <PageGrid columns={{ xs: 1, md: 2, xl: 3 }} strategy={splitStrategy}>
            <>
              <OrganisationProjectDefaultSettingsBox
                organisationId={organisation.id}
                organisationSettings={organisationSettings}
              />
              <OrganisationProjectImportSettingsBox />
              <RestrictedByCapabilityWithDebug capability="REFERRAL">
                <OrganisationReferralBox />
              </RestrictedByCapabilityWithDebug>
              <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                <OrganisationAutomatedWorkflowsBox
                  workflows={organisationWorkflows}
                  emailTemplates={organisationEmailTemplates}
                  organisationId={organisation.id}
                />
              </RestrictedByOrganisationPermissionWithDebug>
            </>
            <>
              <OrganisationCustomFieldConfigBox />
              {organisationEmailSettings && (
                <OrganisationEmailSettingsBox
                  organisation={organisation}
                  organisationEmailSettings={organisationEmailSettings}
                  organisationEmailTemplates={organisationEmailTemplates}
                />
              )}
            </>
            <>
              <RestrictedByCapabilityWithDebug capability="INTEGRATIONS">
                <OrganisationIntegrationsBox
                  organisationId={organisation.id}
                  userId={user.id}
                  allAvailableIntegrations={integrations}
                  organisationIntegrations={organisationIntegrations}
                />
              </RestrictedByCapabilityWithDebug>
              <RestrictedByCapabilityWithDebug capability="TEMPLATING">
                <OrganisationEmbeddableWizardSnippetBox
                  organisation={organisation}
                  organisationSettings={organisationSettings}
                />
              </RestrictedByCapabilityWithDebug>
              {/* <OrganisationAgreementTemplatesBox /> */}
              <button
                type="button"
                disabled={invalidatePrefixOrgCacheLoading}
                onClick={() => {
                  invalidatePrefixOrgCache();
                }}
              >
                invalidate organisation cache{" "}
                {invalidatePrefixOrgCacheLoading ? "..." : ""}
              </button>
            </>
          </PageGrid>
        </PageContainer>
      )}
    </Page>
  );
};
